import posthog from "src/consent/posthog"
import ApiHandler from "src/api/ApiHandler"
import EventAdaptingStateHandler from "src/states/EventAdaptingStateHandler"
import StateConstants from "src/states/StateContstants"
import { singleUnlockPriceEur } from "src/components/reports/reportPricing"
import { applyUserInfo, currentReturnPath } from "./Subscription"

export const RETURN_PARAM = "unlock"
export const RETURN_VALUE = "return"

/** The home and page a signed-out visitor asked to unlock, picked up again once they sign in. */
export const PENDING_UNLOCK_KEY = "pendingPropertyUnlock"
/** The home sent to Stripe, read back on the return to record the payment against it. */
export const UNLOCK_CHECKOUT_KEY = "propertyUnlockCheckout"

export type PropertyUnlockReturnStatus = "unlocking" | "unlocked" | "delayed" | null

interface PendingUnlock {
    propertyId: string
    returnPath: string
}

export function isPropertyUnlocked(stateHandler: EventAdaptingStateHandler, propertyId: string | null | undefined): boolean {
    if (!propertyId) return false
    const unlocked: string[] = stateHandler.get(StateConstants.FREE_UNLOCKED_PROPERTY_IDS) || []
    return unlocked.includes(propertyId)
}

/** Whether every figure on this home's page is shown: a paid account, or this home unlocked. */
export function canSeeHome(stateHandler: EventAdaptingStateHandler, propertyId: string | null | undefined): boolean {
    return !!stateHandler.get(StateConstants.IS_PREMIUM) || isPropertyUnlocked(stateHandler, propertyId)
}

/** Whether the panel should offer this home on its own: the visitor is in the offer's arm and has no access to it yet. */
export function isPropertyUnlockOffered(stateHandler: EventAdaptingStateHandler, propertyId: string | null | undefined): boolean {
    return !!propertyId && singleUnlockPriceEur() != null && !canSeeHome(stateHandler, propertyId)
}

function readPending(): PendingUnlock | null {
    try {
        const parsed = JSON.parse(localStorage.getItem(PENDING_UNLOCK_KEY) || "null")
        return parsed && typeof parsed.propertyId === "string" && typeof parsed.returnPath === "string" ? parsed : null
    } catch {
        return null
    }
}

export async function startPropertyUnlockCheckout(
    apiHandler: ApiHandler,
    stateHandler: EventAdaptingStateHandler,
    propertyId: string,
    returnPath: string = currentReturnPath(),
) {
    const isLoggedIn = !!stateHandler.get(StateConstants.IS_LOGGED_IN)
    posthog?.capture("single_unlock_checkout_started", {
        option: "one_home",
        property_id: propertyId,
        price_eur: singleUnlockPriceEur(),
        is_logged_in: isLoggedIn,
    })

    if (!isLoggedIn) {
        localStorage.setItem(PENDING_UNLOCK_KEY, JSON.stringify({ propertyId, returnPath }))
        stateHandler.set(StateConstants.SHOW_PLAN_MODAL, false)
        stateHandler.set(StateConstants.LOGIN_MODAL_MODE, "signup")
        stateHandler.set(StateConstants.SHOW_LOGIN_MODAL, true)
        return
    }

    const response = await apiHandler.createPropertyUnlockCheckout(propertyId, returnPath)
    if (response?.status === "ready" && response.url) {
        localStorage.setItem(UNLOCK_CHECKOUT_KEY, propertyId)
        window.location.assign(response.url)
        return
    }
    if (response?.status === "already_unlocked") {
        const userInfo = await apiHandler.refreshUserInfo()
        if (userInfo) applyUserInfo(stateHandler, userInfo)
        stateHandler.set(StateConstants.SHOW_PLAN_MODAL, false)
        return
    }
    throw new Error("Could not start the unlock checkout")
}

export async function resumePendingPropertyUnlock(apiHandler: ApiHandler, stateHandler: EventAdaptingStateHandler): Promise<boolean> {
    const pending = readPending()
    if (!pending || !stateHandler.get(StateConstants.IS_LOGGED_IN)) return false
    localStorage.removeItem(PENDING_UNLOCK_KEY)
    if (canSeeHome(stateHandler, pending.propertyId)) return false
    await startPropertyUnlockCheckout(apiHandler, stateHandler, pending.propertyId, pending.returnPath)
    return true
}

export function isPropertyUnlockReturn(): boolean {
    return new URLSearchParams(window.location.search).get(RETURN_PARAM) === RETURN_VALUE
}

export function clearPropertyUnlockReturnParam() {
    const params = new URLSearchParams(window.location.search)
    params.delete(RETURN_PARAM)
    const query = params.toString()
    window.history.replaceState(window.history.state, "", window.location.pathname + (query ? `?${query}` : "") + window.location.hash)
}

/** Waits for the webhook to record the unlock, then shows the home; the paid step is sent once, from here. */
export async function awaitPropertyUnlock(
    apiHandler: ApiHandler,
    stateHandler: EventAdaptingStateHandler,
    attempts: number = 10,
    intervalMs: number = 1000,
): Promise<boolean> {
    const propertyId = localStorage.getItem(UNLOCK_CHECKOUT_KEY)
    if (!propertyId) return false
    stateHandler.set(StateConstants.PROPERTY_UNLOCK_RETURN_STATUS, "unlocking")
    for (let attempt = 0; attempt < attempts; attempt++) {
        try {
            const userInfo = await apiHandler.refreshUserInfo()
            if (userInfo && (userInfo.free_unlocked_property_ids ?? []).includes(propertyId)) {
                applyUserInfo(stateHandler, userInfo)
                localStorage.removeItem(UNLOCK_CHECKOUT_KEY)
                stateHandler.set(StateConstants.PROPERTY_UNLOCK_RETURN_STATUS, "unlocked")
                posthog?.capture("single_unlock_completed", { property_id: propertyId, attempts: attempt + 1 })
                posthog?.capture("single_unlock_paid", { property_id: propertyId, price_eur: singleUnlockPriceEur() })
                return true
            }
        } catch (error) {
            console.error("Failed to read the unlock after checkout", error)
        }
        if (attempt < attempts - 1) {
            await new Promise(resolve => setTimeout(resolve, intervalMs))
        }
    }
    stateHandler.set(StateConstants.PROPERTY_UNLOCK_RETURN_STATUS, "delayed")
    return false
}
